import { useState, useEffect, useMemo, useCallback } from 'react';
import { DateValueType, getPeriodDates } from '@lib/utils';
import { ReportPeriod, ReportGroupBy } from '@src/types/reports';

export interface UseReportFiltersConfig {
  defaultPeriod?: string;
  defaultGroupBy?: string;
  enableProducts?: boolean;
  enableUserGroups?: boolean;
  enableCategories?: boolean;
  enableFiles?: boolean;
  enableSignupForms?: boolean;
  enableInvoiceStatus?: boolean;
  enableCountries?: boolean;
  enableCities?: boolean;
  enableComparisons?: boolean;
  enableCoupons?: boolean;
  enableProductComparing?: boolean;
}

export interface UseReportFiltersReturn {
  // Period filters
  selectedPeriod: string;
  setSelectedPeriod: (period: string) => void;
  periodRange: string;

  // Date range
  valueStart: DateValueType;
  setValueStart: (value: DateValueType) => void;

  // Computed dates for API (with time boundaries)
  startDate: Date | null;
  endDate: Date | null;

  // Group by
  selectedGroupBy: string;
  setSelectedGroupBy: (groupBy: string) => void;

  // Products
  selectedProducts: string[];
  setSelectedProducts: (products: string[]) => void;

  // User groups
  selectedUserGroups: string[];
  setSelectedUserGroups: (groups: string[]) => void;

  // Categories
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;

  // Files
  selectedFiles: string[];
  setSelectedFiles: (files: string[]) => void;

  // Signup Forms
  selectedSignupForms: string[];
  setSelectedSignupForms: (forms: string[]) => void;

  // Invoice status
  selectedInvoiceStatus: string;
  setSelectedInvoiceStatus: (status: string) => void;

  // Countries
  selectedCountries: string[];
  setSelectedCountries: (countries: string[]) => void;

  // Cities
  selectedCities: string[];
  setSelectedCities: (cities: string[]) => void;

  // Coupons
  selectedCoupon: string;
  setSelectedCoupon: (coupons: string) => void;

  // Product Comparing (for Product Conversion report)
  selectedProductA: string;
  setSelectedProductA: (product: string) => void;
  selectedProductB: string;
  setSelectedProductB: (product: string) => void;

  // Comparison flags
  compareToPreviousPeriod: boolean;
  setCompareToPreviousPeriod: (value: boolean) => void;
  compareToSamePeriodPreviousYear: boolean;
  setCompareToSamePeriodPreviousYear: (value: boolean) => void;

  // Visibility helpers
  showPreviousPeriodCheckbox: boolean;
  showPreviousYearCheckbox: boolean;

  // Loading state
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;

  // Reset all filters
  resetFilters: () => void;
}

export const useReportFilters = (
  config: UseReportFiltersConfig = {}
): UseReportFiltersReturn => {
  const {
    defaultPeriod = ReportPeriod.THIS_YEAR,
    defaultGroupBy = ReportGroupBy.MONTH,
    enableComparisons = true,
  } = config;

  const [selectedPeriod, setSelectedPeriod] = useState<string>(defaultPeriod);
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [periodRange, setPeriodRange] = useState<string>('');

  const [selectedGroupBy, setSelectedGroupBy] =
    useState<string>(defaultGroupBy);

  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedUserGroups, setSelectedUserGroups] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [selectedSignupForms, setSelectedSignupForms] = useState<string[]>([]);
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] =
    useState<string>('all');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedCoupon, setSelectedCoupon] = useState<string>('');

  const [selectedProductA, setSelectedProductA] = useState<string>('');
  const [selectedProductB, setSelectedProductB] = useState<string>('');

  const [compareToPreviousPeriod, setCompareToPreviousPeriod] = useState(false);
  const [compareToSamePeriodPreviousYear, setCompareToSamePeriodPreviousYear] =
    useState(false);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const result = getPeriodDates(selectedPeriod, valueStart);

    setPeriodRange(result?.rangeString ?? '');
  }, [selectedPeriod, valueStart]);

  // Compute start and end dates with time boundaries based on selected period
  const { startDate, endDate } = useMemo(() => {
    const dates = getPeriodDates(selectedPeriod, valueStart);

    if (!dates) {
      return { startDate: null, endDate: null };
    }

    return { startDate: dates.startDate, endDate: dates.endDate };
  }, [selectedPeriod, valueStart]);

  const showPreviousPeriodCheckbox = useMemo(() => {
    if (!enableComparisons) return false;

    return (
      selectedPeriod !== ReportPeriod.ALL_TIME &&
      selectedPeriod !== ReportPeriod.EXACT &&
      selectedPeriod !== ReportPeriod.LAST_12_MONTHS
    );
  }, [selectedPeriod, enableComparisons]);

  const showPreviousYearCheckbox = useMemo(() => {
    if (!enableComparisons) return false;

    return (
      selectedPeriod !== ReportPeriod.ALL_TIME &&
      selectedPeriod !== ReportPeriod.EXACT &&
      selectedPeriod !== ReportPeriod.THIS_YEAR &&
      selectedPeriod !== ReportPeriod.LAST_CALENDAR_YEAR &&
      selectedPeriod !== ReportPeriod.LAST_12_MONTHS
    );
  }, [selectedPeriod, enableComparisons]);

  const resetFilters = useCallback(() => {
    setSelectedPeriod(defaultPeriod);
    setSelectedGroupBy(defaultGroupBy);
    setValueStart(null);
    setSelectedProducts([]);
    setSelectedUserGroups([]);
    setSelectedCategories([]);
    setSelectedFiles([]);
    setSelectedProductA('');
    setSelectedProductB('');
    setCompareToPreviousPeriod(false);
    setCompareToSamePeriodPreviousYear(false);
  }, [defaultPeriod, defaultGroupBy]);

  return {
    selectedPeriod,
    setSelectedPeriod,
    periodRange,
    valueStart,
    setValueStart,
    startDate,
    endDate,
    selectedGroupBy,
    setSelectedGroupBy,
    selectedProducts,
    setSelectedProducts,
    selectedUserGroups,
    setSelectedUserGroups,
    selectedCategories,
    setSelectedCategories,
    selectedFiles,
    setSelectedFiles,
    selectedSignupForms,
    setSelectedSignupForms,
    selectedInvoiceStatus,
    setSelectedInvoiceStatus,
    selectedCountries,
    setSelectedCountries,
    selectedCities,
    setSelectedCities,
    selectedCoupon,
    setSelectedCoupon,
    selectedProductA,
    setSelectedProductA,
    selectedProductB,
    setSelectedProductB,
    compareToPreviousPeriod,
    setCompareToPreviousPeriod,
    compareToSamePeriodPreviousYear,
    setCompareToSamePeriodPreviousYear,
    showPreviousPeriodCheckbox,
    showPreviousYearCheckbox,
    isLoading,
    setIsLoading,
    resetFilters,
  };
};
